"use client";

import { c as _c } from "react/compiler-runtime";
import * as React from "react";
import Image from "next/image";
import { motion } from "motion/react";
import { ArrowDown, ArrowRight, Check, Palette } from "lucide-react";
import { Link } from "@/navigation";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useTranslations } from "next-intl";
import { type QrTemplatePreviewLocale, STORED_QR_TEMPLATE_PRESETS, getStoredQrTemplatePreviewImageUrl } from "@/lib/qr-template-registry";
interface PocketCardsShowcaseProps {
  locale: string;
  autoExpand?: boolean;
  squareCardPreviews?: boolean;
  copy?: Partial<{
    badge: string;
    title: string;
    body: string;
    subtitle: string;
    bottomNote: string;
  }>;
  expandedCardIds?: string[];
  stackCardIds?: string[];
  theme?: {
    sectionStyle?: React.CSSProperties;
    badgeStyle?: React.CSSProperties;
    titleStyle?: React.CSSProperties;
    bodyStyle?: React.CSSProperties;
    subtitleStyle?: React.CSSProperties;
    loginButtonClassName?: string;
    loginButtonStyle?: React.CSSProperties;
    loginButtonVariant?: React.ComponentProps<typeof Button>["variant"];
    expandButtonClassName?: string;
    expandButtonStyle?: React.CSSProperties;
  };
}
const supportedLocales = new Set(["en", "da", "no", "sv", "de"]);
const DEFAULT_EXPANDED_CARD_IDS = ["balloons-qr-card", "default-qr-card", "eucalyptus-qr-card", "flower-qr-card", "gatsby-qr-card"] as const;
const DEFAULT_STACK_CARD_IDS = ["eucalyptus-qr-card", "default-qr-card", "balloons-qr-card"] as const;
const stackCardClasses = ["left-1/2 top-1/2 -translate-x-[65%] -translate-y-[55%] -rotate-6", "left-1/2 top-1/2 -translate-x-[50%] -translate-y-[50%] rotate-0 z-10", "left-1/2 top-1/2 -translate-x-[35%] -translate-y-[45%] rotate-6"] as const;
function getExpandedCardOrigin(index: number) {
  return {
    x: 220 - index * 52,
    y: -170 + Math.min(index, 4) * 8,
    rotate: -9 + index * 2.5
  };
}
function resolveCardsByIds<T extends {
  id: string;
}>(cards: T[], ids: readonly string[]) {
  return ids.map(id => cards.find(card => card.id === id)).filter((card): card is T => Boolean(card));
}
export default function PocketCardsShowcase(t0) {
  const $ = _c(156);
  const {
    locale,
    autoExpand: t1,
    squareCardPreviews: t2,
    copy,
    expandedCardIds,
    stackCardIds,
    theme
  } = t0;
  const autoExpand = t1 === undefined ? true : t1;
  const squareCardPreviews = t2 === undefined ? false : t2;
  const [isExpanded, setIsExpanded] = React.useState(false);
  const sectionRef = React.useRef(null);
  const hasAutoExpandedRef = React.useRef(false);
  const userToggledRef = React.useRef(false);
  const t = useTranslations("Index.QRTemplates");
  const tPrint = useTranslations("Home.Event.Templates.printYourself");
  const previewLocale = supportedLocales.has(locale) ? locale as QrTemplatePreviewLocale : "en";
  let t3;
  if ($[0] !== previewLocale) {
    t3 = STORED_QR_TEMPLATE_PRESETS.filter(preset => preset.locales.includes(previewLocale)).map(preset_0 => ({
      id: preset_0.id,
      label: preset_0.name,
      src: getStoredQrTemplatePreviewImageUrl(preset_0, previewLocale),
      variants: preset_0.templateJson.config.variants.map(_temp),
      hasTasks: true
    }));
    $[0] = previewLocale;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const allCards = t3;
  let t4;
  if ($[2] !== expandedCardIds) {
    t4 = expandedCardIds && expandedCardIds.length > 0 ? expandedCardIds.slice(0, 6) : Array.from(DEFAULT_EXPANDED_CARD_IDS);
    $[2] = expandedCardIds;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const resolvedExpandedCardIds = t4;
  let t5;
  if ($[4] !== allCards || $[5] !== resolvedExpandedCardIds) {
    t5 = resolveCardsByIds(allCards, resolvedExpandedCardIds.slice(0, 5));
    $[4] = allCards;
    $[5] = resolvedExpandedCardIds;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const expandedCards = t5;
  let t6;
  if ($[7] !== allCards || $[8] !== resolvedExpandedCardIds) {
    t6 = resolveCardsByIds(allCards, resolvedExpandedCardIds);
    $[7] = allCards;
    $[8] = resolvedExpandedCardIds;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const mobileExpandedCards = t6;
  let t7;
  if ($[10] !== allCards || $[11] !== stackCardIds) {
    const ids = stackCardIds && stackCardIds.length > 0 ? stackCardIds.slice(0, 3) : Array.from(DEFAULT_STACK_CARD_IDS);
    t7 = resolveCardsByIds(allCards, ids);
    $[10] = allCards;
    $[11] = stackCardIds;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const resolved = t7;
  let t8;
  if ($[13] !== expandedCards || $[14] !== resolved) {
    t8 = resolved.length > 0 ? resolved : expandedCards.slice(0, 3);
    $[13] = expandedCards;
    $[14] = resolved;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  const stackCards = t8;
  let t9;
  if ($[16] !== t) {
    t9 = t("badge");
    $[16] = t;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== t) {
    t10 = t("title");
    $[18] = t;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== t) {
    t11 = t("body");
    $[20] = t;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== t) {
    t12 = t("subtitle");
    $[22] = t;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] !== t) {
    t13 = t("moreNote");
    $[24] = t;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] !== copy || $[27] !== t10 || $[28] !== t11 || $[29] !== t12 || $[30] !== t13 || $[31] !== t9) {
    t14 = {
      badge: t9,
      title: t10,
      body: t11,
      subtitle: t12,
      bottomNote: t13,
      ...copy
    };
    $[26] = copy;
    $[27] = t10;
    $[28] = t11;
    $[29] = t12;
    $[30] = t13;
    $[31] = t9;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  const content = t14;
  let t15;
  if ($[33] !== expandedCards || $[34] !== mobileExpandedCards || $[35] !== stackCards) {
    const byId = new Map();
    [...stackCards, ...expandedCards, ...mobileExpandedCards].forEach(card => {
      byId.set(card.id, card);
    });
    t15 = Array.from(byId.values());
    $[33] = expandedCards;
    $[34] = mobileExpandedCards;
    $[35] = stackCards;
    $[36] = t15;
  } else {
    t15 = $[36];
  }
  const preloadCards = t15;
  let t16;
  let t17;
  if ($[37] !== preloadCards) {
    t16 = () => {
      const preloadExpandedCards = () => {
        preloadCards.forEach(_temp2);
      };
      if ("requestIdleCallback" in window) {
        const idleId = window.requestIdleCallback(preloadExpandedCards, {
          timeout: 1200
        });
        return () => window.cancelIdleCallback(idleId);
      }
      const timeoutId = setTimeout(preloadExpandedCards, 350);
      return () => clearTimeout(timeoutId);
    };
    t17 = [preloadCards];
    $[37] = preloadCards;
    $[38] = t16;
    $[39] = t17;
  } else {
    t16 = $[38];
    t17 = $[39];
  }
  React.useEffect(t16, t17);
  let t18;
  let t19;
  if ($[40] !== autoExpand) {
    t18 = () => {
      const sectionElement = sectionRef.current;
      if (!autoExpand || !sectionElement || hasAutoExpandedRef.current || userToggledRef.current) {
        return;
      }
      const tryAutoExpand = (triggerRatio, entry) => {
        if (hasAutoExpandedRef.current || userToggledRef.current) {
          return false;
        }
        const rect = entry?.boundingClientRect ?? sectionElement.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const triggerLine = viewportHeight * triggerRatio;
        const sectionHasEnteredViewport = rect.top <= triggerLine;
        const sectionStillVisible = rect.bottom >= viewportHeight * 0.2;
        if (!sectionHasEnteredViewport || !sectionStillVisible) {
          return false;
        }
        hasAutoExpandedRef.current = true;
        setIsExpanded(true);
        return true;
      };
      const isMobileViewport = _temp3;
      if (isMobileViewport()) {
        const handleViewportChange = () => {
          if (tryAutoExpand(0.11)) {
            window.removeEventListener("scroll", handleViewportChange);
            window.removeEventListener("resize", handleViewportChange);
          }
        };
        window.addEventListener("scroll", handleViewportChange, {
          passive: true
        });
        window.addEventListener("resize", handleViewportChange);
        handleViewportChange();
        return () => {
          window.removeEventListener("scroll", handleViewportChange);
          window.removeEventListener("resize", handleViewportChange);
        };
      }
      const observer = new IntersectionObserver(t20 => {
        const [entry_0] = t20;
        if (!entry_0?.isIntersecting) {
          return;
        }
        if (tryAutoExpand(0.68, entry_0)) {
          observer.disconnect();
        }
      }, {
        root: null,
        rootMargin: "0px 0px -36% 0px",
        threshold: 0.68
      });
      observer.observe(sectionElement);
      return () => observer.disconnect();
    };
    t19 = [autoExpand];
    $[40] = autoExpand;
    $[41] = t18;
    $[42] = t19;
  } else {
    t18 = $[41];
    t19 = $[42];
  }
  React.useEffect(t18, t19);
  let t20;
  if ($[43] !== isExpanded) {
    t20 = () => {
      userToggledRef.current = true;
      setIsExpanded(!isExpanded);
    };
    $[43] = isExpanded;
    $[44] = t20;
  } else {
    t20 = $[44];
  }
  const handleExpandToggle = t20;
  let t21;
  if ($[45] !== isExpanded || $[46] !== squareCardPreviews || $[47] !== tPrint) {
    t21 = (card_1, index, keySuffix) => {
      const origin = getExpandedCardOrigin(index);
      return <motion.div key={`${card_1.id}${keySuffix ?? ""}`} initial={false} animate={isExpanded ? {
        opacity: 1,
        x: 0,
        y: 0,
        rotate: 0,
        scale: 1
      } : {
        opacity: 0,
        x: origin.x,
        y: origin.y,
        rotate: origin.rotate,
        scale: 0.72
      }} transition={{
        type: "spring",
        stiffness: 240,
        damping: 24,
        delay: isExpanded ? index * 0.03 : 0
      }} className={cn("flex h-full w-full max-w-[152px] flex-col sm:max-w-[168px] md:max-w-[176px]", squareCardPreviews ? "bg-transparent" : "rounded-2xl bg-white p-2 shadow-md")}><div className={cn("overflow-hidden", squareCardPreviews ? "rounded-none" : "rounded-xl")}><Image alt={`${card_1.label} print-ready card`} className={cn("h-auto w-full", squareCardPreviews ? "rounded-none" : "rounded-xl")} height={960} loading="eager" sizes="(min-width: 1024px) 176px, (min-width: 640px) 168px, 152px" src={card_1.src} width={640} /></div><div className="mt-auto space-y-2 px-1 pb-1 pt-3"><div className="flex flex-wrap items-center gap-1.5"><span className="text-[10px] font-semibold uppercase tracking-[0.12em] text-[#7a6b5f]">{tPrint("colorVariants")}</span><div className="flex flex-wrap gap-1.5">{card_1.variants.slice(0, 4).map(_temp4)}</div></div>{card_1.hasTasks ? <div className="flex items-end gap-1.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-[#7a6b5f]"><span>{tPrint("photoChallenges")}</span><Check className="mb-px h-3.5 w-3.5 shrink-0 self-end text-[#5d8a62]" /></div> : null}</div></motion.div>;
    };
    $[45] = isExpanded;
    $[46] = squareCardPreviews;
    $[47] = tPrint;
    $[48] = t21;
  } else {
    t21 = $[48];
  }
  const renderExpandedCard = t21;
  const t22 = theme?.sectionStyle;
  const t23 = theme?.badgeStyle;
  let t24;
  if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = <Palette className="h-3.5 w-3.5" />;
    $[49] = t24;
  } else {
    t24 = $[49];
  }
  let t25;
  if ($[50] !== content.badge || $[51] !== t23) {
    t25 = <div className="inline-flex items-center gap-2 self-start rounded-full bg-primary/10 px-4 py-1.5 text-sm font-medium text-primary" style={t23}>{t24}{content.badge}</div>;
    $[50] = content.badge;
    $[51] = t23;
    $[52] = t25;
  } else {
    t25 = $[52];
  }
  const t26 = theme?.titleStyle;
  let t27;
  if ($[53] !== content.title || $[54] !== t26) {
    t27 = <h2 className="text-3xl font-bold md:text-4xl" style={t26}>{content.title}</h2>;
    $[53] = content.title;
    $[54] = t26;
    $[55] = t27;
  } else {
    t27 = $[55];
  }
  const t28 = theme?.bodyStyle;
  let t29;
  if ($[56] !== content.body || $[57] !== t28) {
    t29 = <p className="text-lg leading-relaxed text-muted-foreground" style={t28}>{content.body}</p>;
    $[56] = content.body;
    $[57] = t28;
    $[58] = t29;
  } else {
    t29 = $[58];
  }
  const t30 = theme?.subtitleStyle;
  let t31;
  if ($[59] !== content.subtitle || $[60] !== t30) {
    t31 = <p className="text-sm text-muted-foreground" style={t30}>{content.subtitle}</p>;
    $[59] = content.subtitle;
    $[60] = t30;
    $[61] = t31;
  } else {
    t31 = $[61];
  }
  const t32 = theme?.loginButtonVariant ?? "dark";
  const t33 = theme?.loginButtonClassName;
  let t34;
  if ($[62] !== t33) {
    t34 = cn("w-full sm:w-fit", t33);
    $[62] = t33;
    $[63] = t34;
  } else {
    t34 = $[63];
  }
  const t35 = theme?.loginButtonStyle;
  let t36;
  if ($[64] !== t) {
    t36 = t("loginButton");
    $[64] = t;
    $[65] = t36;
  } else {
    t36 = $[65];
  }
  let t37;
  if ($[66] === Symbol.for("react.memo_cache_sentinel")) {
    t37 = <ArrowRight className="h-4 w-4" />;
    $[66] = t37;
  } else {
    t37 = $[66];
  }
  let t38;
  if ($[67] !== t36) {
    t38 = <Link href="/login"><span className="flex items-center gap-2">{t36}{t37}</span></Link>;
    $[67] = t36;
    $[68] = t38;
  } else {
    t38 = $[68];
  }
  let t39;
  if ($[69] !== t32 || $[70] !== t34 || $[71] !== t35 || $[72] !== t38) {
    t39 = <Button asChild={true} variant={t32} className={t34} style={t35}>{t38}</Button>;
    $[69] = t32;
    $[70] = t34;
    $[71] = t35;
    $[72] = t38;
    $[73] = t39;
  } else {
    t39 = $[73];
  }
  const t40 = theme?.expandButtonClassName;
  let t41;
  if ($[74] !== t40) {
    t41 = cn("w-full sm:w-fit", t40);
    $[74] = t40;
    $[75] = t41;
  } else {
    t41 = $[75];
  }
  const t42 = theme?.expandButtonStyle;
  let t43;
  if ($[76] !== isExpanded || $[77] !== t) {
    t43 = isExpanded ? t("collapseButton") : t("expandButton");
    $[76] = isExpanded;
    $[77] = t;
    $[78] = t43;
  } else {
    t43 = $[78];
  }
  const t44 = isExpanded ? 180 : 0;
  let t45;
  if ($[79] !== t44) {
    t45 = {
      rotate: t44
    };
    $[79] = t44;
    $[80] = t45;
  } else {
    t45 = $[80];
  }
  let t46;
  if ($[81] === Symbol.for("react.memo_cache_sentinel")) {
    t46 = {
      duration: 0.24,
      ease: "easeOut"
    };
    $[81] = t46;
  } else {
    t46 = $[81];
  }
  let t47;
  if ($[82] === Symbol.for("react.memo_cache_sentinel")) {
    t47 = <ArrowDown className="h-4 w-4" />;
    $[82] = t47;
  } else {
    t47 = $[82];
  }
  let t48;
  if ($[83] !== t45) {
    t48 = <motion.span animate={t45} transition={t46} className="inline-flex">{t47}</motion.span>;
    $[83] = t45;
    $[84] = t48;
  } else {
    t48 = $[84];
  }
  let t49;
  if ($[85] !== t43 || $[86] !== t48) {
    t49 = <span className="flex items-center gap-2">{t43}{t48}</span>;
    $[85] = t43;
    $[86] = t48;
    $[87] = t49;
  } else {
    t49 = $[87];
  }
  let t50;
  if ($[88] !== handleExpandToggle || $[89] !== isExpanded || $[90] !== t41 || $[91] !== t42 || $[92] !== t49) {
    t50 = <Button variant="outline" className={t41} style={t42} onClick={handleExpandToggle} aria-expanded={isExpanded} aria-controls="pocket-card-expanded">{t49}</Button>;
    $[88] = handleExpandToggle;
    $[89] = isExpanded;
    $[90] = t41;
    $[91] = t42;
    $[92] = t49;
    $[93] = t50;
  } else {
    t50 = $[93];
  }
  let t51;
  if ($[94] !== t39 || $[95] !== t50) {
    t51 = <div className="flex flex-col gap-3 sm:flex-row">{t39}{t50}</div>;
    $[94] = t39;
    $[95] = t50;
    $[96] = t51;
  } else {
    t51 = $[96];
  }
  let t52;
  if ($[97] !== t25 || $[98] !== t27 || $[99] !== t29 || $[100] !== t31 || $[101] !== t51) {
    t52 = <div className="flex flex-col gap-6">{t25}{t27}{t29}{t31}{t51}</div>;
    $[97] = t25;
    $[98] = t27;
    $[99] = t29;
    $[100] = t31;
    $[101] = t51;
    $[102] = t52;
  } else {
    t52 = $[102];
  }
  const t53 = isExpanded ? "max-h-[2200px] opacity-100" : "max-h-0 opacity-0";
  let t54;
  if ($[103] !== t53) {
    t54 = cn("overflow-hidden transition-[max-height,opacity,padding] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] md:hidden", t53);
    $[103] = t53;
    $[104] = t54;
  } else {
    t54 = $[104];
  }
  let t55;
  if ($[105] !== mobileExpandedCards || $[106] !== renderExpandedCard) {
    let t56;
    if ($[108] !== renderExpandedCard) {
      t56 = (card_2, index_0) => renderExpandedCard(card_2, index_0, "-mobile");
      $[108] = renderExpandedCard;
      $[109] = t56;
    } else {
      t56 = $[109];
    }
    t55 = mobileExpandedCards.map(t56);
    $[105] = mobileExpandedCards;
    $[106] = renderExpandedCard;
    $[107] = t55;
  } else {
    t55 = $[107];
  }
  let t56;
  if ($[110] !== t55) {
    t56 = <div className="grid grid-cols-2 justify-items-center gap-3">{t55}</div>;
    $[110] = t55;
    $[111] = t56;
  } else {
    t56 = $[111];
  }
  let t57;
  if ($[112] !== content.bottomNote || $[113] !== theme?.subtitleStyle) {
    t57 = content.bottomNote ? <p className="mx-auto mt-5 max-w-md text-center text-xs leading-relaxed text-muted-foreground" style={theme?.subtitleStyle}>{content.bottomNote}</p> : null;
    $[112] = content.bottomNote;
    $[113] = theme?.subtitleStyle;
    $[114] = t57;
  } else {
    t57 = $[114];
  }
  let t58;
  if ($[115] !== t56 || $[116] !== t57) {
    t58 = <div id="pocket-card-expanded-mobile" className="pt-2">{t56}{t57}</div>;
    $[115] = t56;
    $[116] = t57;
    $[117] = t58;
  } else {
    t58 = $[117];
  }
  let t59;
  if ($[118] !== t54 || $[119] !== t58) {
    t59 = <div className={t54}>{t58}</div>;
    $[118] = t54;
    $[119] = t58;
    $[120] = t59;
  } else {
    t59 = $[120];
  }
  let t60;
  if ($[121] !== squareCardPreviews || $[122] !== stackCards) {
    let t61;
    if ($[124] !== squareCardPreviews) {
      t61 = (card_3, index_1) => <div key={card_3.id} className={cn("absolute w-40 overflow-hidden shadow-lg transition-transform duration-500 hover:scale-[1.01] md:w-48", squareCardPreviews ? "rounded-none" : "rounded-xl", stackCardClasses[index_1], index_1 === 1 ? "shadow-xl" : "")}><Image alt={`${card_3.label} pocket card`} className="h-auto w-full" fetchPriority="high" height={960} priority={index_1 === 1} sizes="(min-width: 768px) 192px, 160px" src={card_3.src} width={640} /></div>;
      $[124] = squareCardPreviews;
      $[125] = t61;
    } else {
      t61 = $[125];
    }
    t60 = stackCards.map(t61);
    $[121] = squareCardPreviews;
    $[122] = stackCards;
    $[123] = t60;
  } else {
    t60 = $[123];
  }
  let t61;
  if ($[126] !== t60) {
    t61 = <div className="relative hidden min-h-[400px] items-center justify-center md:flex">{t60}</div>;
    $[126] = t60;
    $[127] = t61;
  } else {
    t61 = $[127];
  }
  let t62;
  if ($[128] !== t52 || $[129] !== t59 || $[130] !== t61) {
    t62 = <div className="grid grid-cols-1 items-center gap-12 md:grid-cols-2">{t52}{t59}{t61}</div>;
    $[128] = t52;
    $[129] = t59;
    $[130] = t61;
    $[131] = t62;
  } else {
    t62 = $[131];
  }
  const t63 = isExpanded ? "max-h-[1100px] opacity-100" : "max-h-0 opacity-0";
  let t64;
  if ($[132] !== t63) {
    t64 = cn("hidden overflow-hidden transition-[max-height,opacity,padding] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] md:block", t63);
    $[132] = t63;
    $[133] = t64;
  } else {
    t64 = $[133];
  }
  let t65;
  if ($[134] !== expandedCards || $[135] !== renderExpandedCard) {
    let t66;
    if ($[137] !== renderExpandedCard) {
      t66 = (card_4, index_2) => renderExpandedCard(card_4, index_2);
      $[137] = renderExpandedCard;
      $[138] = t66;
    } else {
      t66 = $[138];
    }
    t65 = expandedCards.map(t66);
    $[134] = expandedCards;
    $[135] = renderExpandedCard;
    $[136] = t65;
  } else {
    t65 = $[136];
  }
  let t66;
  if ($[139] !== t65) {
    t66 = <div className="grid grid-cols-5 justify-items-center gap-3 md:gap-4">{t65}</div>;
    $[139] = t65;
    $[140] = t66;
  } else {
    t66 = $[140];
  }
  let t67;
  if ($[141] !== content.bottomNote || $[142] !== theme?.subtitleStyle) {
    t67 = content.bottomNote ? <p className="mx-auto mt-5 max-w-xl text-center text-sm leading-relaxed text-muted-foreground" style={theme?.subtitleStyle}>{content.bottomNote}</p> : null;
    $[141] = content.bottomNote;
    $[142] = theme?.subtitleStyle;
    $[143] = t67;
  } else {
    t67 = $[143];
  }
  let t68;
  if ($[144] !== t66 || $[145] !== t67) {
    t68 = <div id="pocket-card-expanded" className="pt-8">{t66}{t67}</div>;
    $[144] = t66;
    $[145] = t67;
    $[146] = t68;
  } else {
    t68 = $[146];
  }
  let t69;
  if ($[147] !== t64 || $[148] !== t68) {
    t69 = <div className={t64}>{t68}</div>;
    $[147] = t64;
    $[148] = t68;
    $[149] = t69;
  } else {
    t69 = $[149];
  }
  let t70;
  if ($[150] !== t62 || $[151] !== t69) {
    t70 = <div className="mx-auto max-w-screen-lg">{t62}{t69}</div>;
    $[150] = t62;
    $[151] = t69;
    $[152] = t70;
  } else {
    t70 = $[152];
  }
  let t71;
  if ($[153] !== t22 || $[154] !== t70) {
    t71 = <div ref={sectionRef} className="w-full bg-muted/50 px-4 py-20" style={t22}>{t70}</div>;
    $[153] = t22;
    $[154] = t70;
    $[155] = t71;
  } else {
    t71 = $[155];
  }
  return t71;
}
function _temp4(variant_0) {
  return <div key={variant_0.id} title={variant_0.name} className="rounded-full border border-black/6 bg-[#f7f1ea]/70 p-1"><span className="block h-3.5 w-3.5 rounded-full border border-black/10 shadow-[inset_0_1px_0_rgba(255,255,255,0.45)]" style={{
      background: variant_0.style === "gradient" ? `linear-gradient(135deg, ${variant_0.swatchStart}, ${variant_0.swatchEnd})` : variant_0.swatchStart
    }} /></div>;
}
function _temp3() {
  return window.innerWidth < 768;
}
function _temp2(card_0) {
  const image = new window.Image();
  image.decoding = "async";
  image.src = card_0.src;
}
function _temp(variant) {
  return {
    id: variant.id,
    name: variant.name,
    swatchStart: variant.swatchStart,
    swatchEnd: variant.swatchEnd,
    style: variant.style
  };
}